import { ClientType } from "@multiweb/clients/ClientType";
import { IExportImportClient } from "@multiweb/clients/exportImport";
import { IClientFactory } from "@multiweb/clients/IClientFactory";
import { Key } from "Global/Base/Utils/EventUtils";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { FileStreamReader } from "../../../../../Global/Base/Streams/FileStreamReader";
import { FileUtils } from "../../../../../Global/Base/Utils/FileUtils";
import { EventsController } from "../../../../../Global/Dom/EventsController";
import { FileUploader } from "../../../../../OW.Remoting/Remoting/FileUploader";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Control } from "../../Controls/Control";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { MessageWindow } from "../../MessageWindow";
import { ButtonType } from "../Actions/Button";
import { LoaderButton } from "../Actions/LoaderButton";

export class ImportPanel extends Control {
    private _fileElement: HTMLInputElement;
    private _importButton: LoaderButton;
    private _uploadButton: LoaderButton;
    private _uploadFileNameElement: HTMLDivElement;
    private _eventController: EventsController;
    private __client: IExportImportClient | null = null;
    private __currentFileTempId: string | null = null;

    private set _fileName(v: string | null) {
        if (v?.length > 0) this._uploadFileNameElement.textContent = v;
        else this._uploadFileNameElement.textContent = "";

        this._uploadFileNameElement.classList.toggle("filled", v?.length > 0);
    }

    private get _currentFileTempId(): string | null {
        return this.__currentFileTempId;
    }

    private set _currentFileTempId(v: string | null) {
        this.__currentFileTempId = v;
        this._importButton.enabled = v != null;
    }

    /**
     * Klient pro zpracování požadavků na službu
     * @private
     */
    private get _client(): IExportImportClient {
        if (!this.__client) this.__client = DIContainer.instance.create(IClientFactory).get(ClientType.exportImport);

        return this.__client;
    }

    constructor(params: ControlParams) {
        super(params);
        const { element } = params;

        element.classList.add("import-panel");

        this._createElements(element);

        this._registerListeners();
    }

    private _createElements(rootElement: HTMLElement): void {
        this._fileElement = createElement({ type: "input[file]" }) as HTMLInputElement;
        rootElement.appendChild(this._fileElement);

        const container = createElement({ classes: "container" });

        this._importButton = new LoaderButton("GUI-57419", ButtonType.raised, false); // Spustit import
        this._importButton.element.tabIndex = 0;
        this._importButton.enabled = false;

        rootElement.appendChild(container);
        container.appendChild(this._createSubPanel(this._uploadSection()));
        container.appendChild(this._createSubPanel(this._importButton.element));
    }

    private _uploadSection(): HTMLElement {
        this._uploadButton = new LoaderButton("GUI-600106", ButtonType.raised, false); // Nahrát soubor
        this._uploadButton.element.tabIndex = 0;

        const uploadSection = createElement({ classes: "upload-section" });

        const uploadContainerElement = createElement({ classes: "file-container" });
        this._uploadFileNameElement = createElement({ classes: "file-name" }) as HTMLDivElement;

        uploadContainerElement.appendChild(this._uploadFileNameElement);
        uploadContainerElement.appendChild(this._uploadButton.element);
        uploadSection.appendChild(uploadContainerElement);

        return uploadSection;
    }

    private _clearUploadFile(): void {
        // po odeslaní vynulujeme input pro soubory
        this._fileElement.value = null;
        this._uploadButton.enabled = true;
        this._uploadButton.isLoading = false;
    }

    private async _upload(): Promise<void> {
        if (this._fileElement.files.length == 0) return;

        this._uploadButton.enabled = false;
        this._uploadButton.isLoading = true;

        const file = this._fileElement.files[0];

        this._fileName = file.name;

        const streamReader = new FileStreamReader(file);
        const uploader = new FileUploader();

        uploader.onFinished.add((_fu, tmpFileId) => {
            try {
                this._currentFileTempId = tmpFileId;
            } finally {
                // po odeslaní vynulujeme input pro soubory
                this._clearUploadFile();
            }
        });

        uploader.onError.add(async () => {
            this._clearUploadFile();
            this._fileName = await Localization.getTextPromise("GUI-780482"); // Chyba při nahrávání souboru
        });

        return uploader.start(streamReader, () => OWManager.getDocumentUploadUrl(FileUtils.getFileExtension(streamReader.name), streamReader.size));
    }

    private _createSubPanel(element: HTMLElement): HTMLElement {
        const el = createElement({ classes: "panel" });

        el.appendChild(element);

        return el;
    }

    private _registerListeners(): void {
        if (!this._eventController) this._eventController = new EventsController();

        const importButtonClickHandler = async () => {
            try {
                if (!this._currentFileTempId) return;

                this._importButton.isLoading = true;
                this._importButton.enabled = false;
                this._uploadButton.enabled = false;
                await this._client.import(this._currentFileTempId);
                this._currentFileTempId = null;

                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-318366")); // Operace byla spuštěna na pozadí. O výsledku budete informování.
            } catch (e) {
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-769937")); // Operace byla přerušena
            } finally {
                this._uploadButton.enabled = true;
                this._importButton.isLoading = false;
            }
        };

        this._eventController.addListener(this._importButton.element, "click", importButtonClickHandler);
        this._eventController.addListener(this._importButton.element, "keydown", (event: KeyboardEvent) => {
            if (event.key === Key.Space) {
                importButtonClickHandler();
            }
        });

        const uploadButtonClickHandler = () => {
            if (!this._uploadButton.enabled) return;
            this._fileElement.click();
        };

        this._eventController.addListener(this._uploadButton.element, "click", uploadButtonClickHandler);
        this._eventController.addListener(this._uploadButton.element, "keydown", (event: KeyboardEvent) => {
            if (event.key === Key.Space) {
                uploadButtonClickHandler();
            }
        });

        this._eventController.addListener(this._fileElement, "change", () => {
            this._upload();
        });
    }
}
